import { observer } from 'mobx-react';
import React from 'react';
import { authState } from 'rxfire/auth';
import { collectionData, doc } from 'rxfire/firestore';
import { Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { getStoreFromContext, StoreContext } from 'models/utils';
import { CurrentUser } from 'models/CurrentUserModel';
import LoadingScreen from 'screens/LoadingScreen';

@observer
export class WithFirebaseSubscriptions extends React.Component {
  static contextType = StoreContext;
  // declare context: React.ContextType<typeof StoreContext>; // add declare after CRA supports this TS 3.7 feature
  context!: React.ContextType<typeof StoreContext>;

  authSubscription?: Subscription;
  currentUserSubscription?: Subscription;
  picturesSubscriptions?: Subscription;

  subscribeAuth = () => {
    const store = getStoreFromContext(this.context);
    const authRef = store.firebase.auth;

    this.authSubscription =
      this.authSubscription ??
      authState(authRef).subscribe((user) => {
        store.currentUser.setAuthUser(user);
        if (user) {
          store.userPictures.ensureUserPictures();
          this.subscribeCurrentUser();
          this.subscribeCurrentUserPictures();
        }
      });
  };

  subscribeCurrentUser = () => {
    const store = getStoreFromContext(this.context);
    this.currentUserSubscription =
      this.currentUserSubscription ??
      doc(store.currentUser.currentUserRef)
        .pipe(map((user) => user.data()))
        .pipe(filter((user) => !!user))
        .subscribe((user) => {
          store.currentUser.setCurrentUser(user as CurrentUser);
          store.onboarding.runHooks();
        });
  };

  subscribeCurrentUserPictures = () => {
    const store = getStoreFromContext(this.context);

    this.picturesSubscriptions =
      this.picturesSubscriptions ??
      collectionData(store.firebase.pictures.where('user', '==', store.currentUser.currentUserRef)).subscribe(
        (pictures) => {
          store.userPictures.setPictures(pictures);
          store.onboarding.runHooks();
        }
      );
  };

  componentDidMount() {
    const store = getStoreFromContext(this.context);
    this.subscribeAuth();
    store.firebase.auth.getRedirectResult().catch(async (error) => {
      return store.firebase.resolveSignInFailure(error);
    });
  }

  componentWillUnmount() {
    this.authSubscription?.unsubscribe();
    this.picturesSubscriptions?.unsubscribe();
    this.currentUserSubscription?.unsubscribe();
  }

  render() {
    const store = getStoreFromContext(this.context);
    // Don't render routes before the first response from firebase auth
    if (store.currentUser.isLoadingAuth) {
      return <LoadingScreen />;
    }

    return <>{this.props.children}</>;
  }
}
